"use client";

import { useEffect, useState } from "react";
import { Menu, Star, MapPin } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function WiggleRule({
  thick = false
}: {
  thick?: boolean;
}) {
  return <svg className="absolute -bottom-px left-0 w-full" style={{
    height: thick ? "8px" : "6px"
  }} viewBox="0 0 1200 8" preserveAspectRatio="none" aria-hidden="true">
      <path d="M0,4 Q30,0 60,4 T120,4 T180,4 T240,4 T300,4 T360,4 T420,4 T480,4 T540,4 T600,4 T660,4 T720,4 T780,4 T840,4 T900,4 T960,4 T1020,4 T1080,4 T1140,4 T1200,4" fill="none" stroke="var(--foreground)" strokeWidth={thick ? "4" : "3"} strokeLinecap="round" />
    </svg>;
}
function MarkerCircle() {
  return <svg className="absolute -inset-1 w-[calc(100%+8px)] h-[calc(100%+8px)] pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300" viewBox="0 0 100 40" preserveAspectRatio="none" aria-hidden="true">
      <path d="M50,5 C75,5 92,12 92,20 C92,30 70,35 50,35 C28,35 8,30 8,20 C8,12 25,6 50,5" fill="none" stroke="var(--primary)" strokeWidth="2.5" strokeLinecap="round" strokeDasharray="200" strokeDashoffset="0" style={{
      animation: "drawCircle 600ms ease-out forwards"
    }} />
    </svg>;
}
function DesktopNavConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-2">
        <div className="h-9 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-24 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-28 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-1 lg:gap-2">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i} className="flex items-center">
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative px-3 lg:px-4 py-2 font-default text-sm lg:text-[17px] uppercase tracking-wide text-foreground hover:bg-transparent focus:bg-transparent">
                <span className="relative inline-block">
                  {item.label}
                  <MarkerCircle />
                </span>
              </Link>
            </NavigationMenuLink>
            {i < menuItems.length - 1 && <Star className="ml-1 lg:ml-2 h-3 w-3 text-foreground" strokeWidth={3} aria-hidden="true" />}
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNavConsumer({
  onItemClick
}: {
  onItemClick: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-3">
        <div className="h-14 w-full bg-muted animate-pulse rounded-md" />
        <div className="h-14 w-full bg-muted animate-pulse rounded-md" />
        <div className="h-14 w-full bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-3">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} onClick={onItemClick} className="block w-full px-6 py-4 rounded-md bg-background border-[1px] border-border text-foreground font-default text-lg uppercase tracking-wide text-center hover:bg-accent transition-all duration-200">
          {item.label}
        </Link>)}
    </nav>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href) return;
      if (href === "/#") {
        e.preventDefault();
        window.scrollTo({
          top: 0,
          behavior: "smooth"
        });
        return;
      }
      let hash = "";
      if (href.startsWith("#")) {
        hash = href;
      } else if (href.startsWith("/") && href.includes("#")) {
        const [path, hashPart] = href.split("#");
        if (hashPart === "") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        if (path === "/" || path === window.location.pathname) {
          hash = "#" + hashPart;
        }
      }
      if (hash && hash !== "#") {
        const element = document.querySelector(hash);
        if (element) {
          e.preventDefault();
          element.scrollIntoView({
            behavior: "smooth",
            block: "start"
          });
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="1101" id="main-navigation" className={`sticky top-0 z-50 w-full transition-all duration-300 bg-background text-foreground ${scrolled ? "shadow-md" : ""}`}>
      <style>{`
        @keyframes drawCircle {
          from { stroke-dashoffset: 200; }
          to { stroke-dashoffset: 0; }
        }
      `}</style>

      <div className="relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid grid-cols-12 items-center h-[72px] md:h-[88px] gap-2 md:gap-4">
            <div className="col-span-6 md:col-span-3 flex items-center">
              <Link to="/" className="inline-flex items-center" aria-label="Strice Industries Inc. home">
                <WvcLogo className="h-10 md:h-12 w-auto" />
              </Link>
            </div>

            <div className="hidden md:flex col-span-6 items-center justify-center">
              <MenuProvider menu_id="44">
                <DesktopNavConsumer />
              </MenuProvider>
            </div>

            <div className="col-span-6 md:col-span-3 flex items-center justify-end gap-2">
              <Link to="/GetInTouchPage" className="hidden md:inline-flex group relative items-center gap-2 px-6 lg:px-7 py-3 rounded-md bg-primary text-primary-foreground font-default text-sm lg:text-[15px] uppercase tracking-wide hover:shadow-lg transition-all duration-200">
                <MapPin className="h-4 w-4" strokeWidth={3} />
                <span>Contact Us</span>
              </Link>

              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="Open menu" className="md:hidden h-12 w-12 rounded-md bg-background border-[1px] border-border text-foreground hover:bg-accent transition-all duration-200">
                    <Menu className="h-6 w-6" strokeWidth={3} />
                  </Button>
                </SheetTrigger>

                <SheetContent side="right" className="bg-background border-l-[1px] border-border w-[88%] sm:max-w-sm p-0 flex flex-col">
                  <SheetTitle className="sr-only">Navigation Menu</SheetTitle>

                  <div className="relative px-6 pt-6 pb-5">
                    <div className="flex items-center justify-between">
                      <Link to="/" onClick={() => setMobileOpen(false)} aria-label="Strice Industries Inc. home">
                        <WvcLogo className="h-10 w-auto" />
                      </Link>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto px-6 py-8">
                    <MenuProvider menu_id="44">
                      <MobileNavConsumer onItemClick={() => setMobileOpen(false)} />
                    </MenuProvider>

                    <div className="mt-8 flex items-center justify-center gap-3 text-foreground">
                      <Star className="h-5 w-5" strokeWidth={3} />
                      <span className="font-default text-xl inline-block">
                        Precision Industrial
                      </span>
                      <Star className="h-5 w-5" strokeWidth={3} />
                    </div>
                  </div>

                  <div className="p-6 border-t-[1px] border-border bg-background">
                    <Link to="/GetInTouchPage" onClick={() => setMobileOpen(false)} className="group relative w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-md bg-primary text-primary-foreground font-default text-base uppercase tracking-wide hover:shadow-lg transition-all duration-200">
                      <MapPin className="h-5 w-5" strokeWidth={3} />
                      <span>Contact Us</span>
                    </Link>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>
    </section>;
}